/**
 * Hook to fetch daily check-in metrics for the Compare View
 * Fetches emotion, food quality, cycle phase, and cycle symptoms
 * UI-level only - no new calculations or reasoning
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

export interface DailyCheckinMetrics {
  date: string;
  emotion: string | null;
  processed_food_level: number | null;
  healthy_food_level: number | null;
  menstrual_cycle_day: number | null;
  menstrual_symptoms: string[] | null;
  menstrual_flow: string | null;
  stress_level: number | null;
  anxiety_level: number | null;
  mood_score: number | null;
  fatigue_level: number | null;
  pain_level: number | null;
}

// Map cycle day to phase for display
export function getCyclePhaseFromDay(cycleDay: number | null): number | null {
  if (cycleDay === null) return null;
  // Simplified phase mapping: 1-5 menstrual, 6-13 follicular, 14-16 ovulation, 17-28 luteal
  if (cycleDay <= 5) return 1; // Menstrual
  if (cycleDay <= 13) return 2; // Follicular
  if (cycleDay <= 16) return 3; // Ovulation
  return 4; // Luteal
}

// Calculate food quality score from existing fields
export function calculateFoodQualityScore(
  processedLevel: number | null,
  healthyLevel: number | null
): number | null {
  if (processedLevel === null && healthyLevel === null) return null;
  const processed = processedLevel ?? 5;
  const healthy = healthyLevel ?? 5;
  // Higher healthy + lower processed = better score
  return Math.round(((healthy * 10) + ((10 - processed) * 10)) / 2);
}

export function useDailyCheckinMetrics(days: number = 30) {
  return useQuery({
    queryKey: ['daily-checkin-metrics', days],
    queryFn: async (): Promise<DailyCheckinMetrics[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('date, emotion, processed_food_level, healthy_food_level, menstrual_cycle_day, menstrual_symptoms, menstrual_flow, stress_level, anxiety_level, mood_score, fatigue_level, pain_level')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (error) {
        console.error('[useDailyCheckinMetrics] Error:', error);
        return [];
      }

      return (data || []) as DailyCheckinMetrics[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
